import { Link } from 'react-router-dom';
import { useFmt } from '@/lib/useFmt.js';
import type { OrdersResponse } from '@/lib/types.js';
import {
  OrderTable,
  SectionPager,
  SectionTitle,
  planLabel,
  type SectionPaging,
} from './shared.js';

/**
 * 兑换码激活记录 — 只统计, 不计为收入. Paginated because this can grow
 * fast (each Xianyu buyer adds 1 row).
 */
export function RedemptionsSection({
  data,
  offset,
  onOffsetChange,
  pageSize,
  loading,
}: { data: OrdersResponse['redemptions']; loading: boolean } & SectionPaging): JSX.Element {
  const { fmtDateTime } = useFmt();
  return (
    <section className="mb-8">
      <SectionTitle
        icon="🎟️"
        title="兑换码激活记录"
        hint={
          <span className="text-xs px-2 py-0.5 rounded bg-amber-900/30 text-amber-300 font-normal border border-amber-800/50">
            非收入 · 信息性
          </span>
        }
      />
      <p className="text-xs text-slate-500 mb-3">
        这里只是「多少买家 / 测试账号用过兑换码」的统计。这些码可能来自闲鱼售卖、admin 手动开通、或测试。
        <span className="ml-2">
          当前累计 {data.total} 次（月卡 {data.by_plan.monthly} · 年卡 {data.by_plan.yearly}）
        </span>
      </p>

      <OrderTable>
        <thead className="bg-white/[0.04]">
          <tr>
            <th className="px-4 py-3 text-left text-slate-300 font-medium">用户</th>
            <th className="px-4 py-3 text-left text-slate-300 font-medium">套餐</th>
            <th className="px-4 py-3 text-left text-slate-300 font-medium">兑换码</th>
            <th className="px-4 py-3 text-left text-slate-300 font-medium">批次</th>
            <th className="px-4 py-3 text-left text-slate-300 font-medium">激活时间</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                加载中…
              </td>
            </tr>
          ) : data.rows.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                还没人兑换过
              </td>
            </tr>
          ) : (
            data.rows.map((r) => (
              <tr key={r.code} className="border-t border-white/[0.08]">
                <td className="px-4 py-3">
                  <Link to={`/users/${r.user_id}`} className="text-shark-300 hover:text-shark-200">
                    {r.user_email ?? r.user_id.slice(0, 8)}
                  </Link>
                </td>
                <td className="px-4 py-3 text-slate-300">{planLabel(r.plan)}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-400">{r.code}</td>
                <td className="px-4 py-3 text-slate-400">{r.batch_id ?? '—'}</td>
                <td className="px-4 py-3 text-slate-400">{fmtDateTime(r.activated_at)}</td>
              </tr>
            ))
          )}
        </tbody>
      </OrderTable>

      <SectionPager total={data.total} paging={{ offset, onOffsetChange, pageSize }} />
    </section>
  );
}
